'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

/**
 * StaleContentDetector Component
 * 
 * Automatically refreshes page content when the tab becomes visible
 * after being inactive for 30+ minutes.
 * 
 * This ensures users always see fresh content when returning to the site.
 */
export default function StaleContentDetector() {
  const router = useRouter();
  const lastVisibleTimeRef = useRef<number>(Date.now());
  const hasRefreshedRef = useRef<boolean>(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is now hidden - record the time
        lastVisibleTimeRef.current = Date.now();
        hasRefreshedRef.current = false;
      } else {
        // Tab is now visible - check if it was hidden for 30+ minutes
        const now = Date.now();
        const timeHidden = now - lastVisibleTimeRef.current;
        const thirtyMinutes = 30 * 60 * 1000;

        if (timeHidden >= thirtyMinutes && !hasRefreshedRef.current) {

          hasRefreshedRef.current = true;

          // Use Next.js router.refresh() to refresh server components
          // This fetches fresh data without a full page reload
          router.refresh();
        }
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also listen for focus events as a backup
    const handleFocus = () => {
      if (!document.hidden) {
        handleVisibilityChange();
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [router]);

  // This component is invisible - it only manages behavior
  return null;
}

